import { notifyError } from "@/lib/notify-error";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ColorPicker } from "@/components/ui/color-picker";
import type { TagRecord } from "@/types/takeoff-pilot";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import {
  UNIT_COST_DECIMAL_PATTERN,
  getEffectiveCostBasis,
  formatCurrency,
} from "@/features/takeoff-pilot/takeoff-utils";
import {
  TAKEOFF_COST_BASIS,
  type TakeoffCostBasis,
} from "@/lib/takeoff-domain";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";
import { SegmentedControl } from "@/components/ui/segmented-control";

const PRICING_MODE_STACK_CLASSES =
  "h-auto w-10 flex-col gap-px p-px [&>[data-slot=segmented-control-item]]:h-4 [&>[data-slot=segmented-control-item]]:px-1 [&>[data-slot=segmented-control-item]]:text-[10px] [&>[data-slot=segmented-control-item]]:leading-none";

function getTextareaMirrorValue(value: string) {
  if (!value) return " ";
  return value.endsWith("\n") ? `${value} ` : value;
}

export function UnitCostInput({
  tag,
  disabled,
  setUnitCost,
  setCostBasis,
}: {
  tag: TagRecord;
  disabled: boolean;
  setUnitCost: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    unitCost: number | null;
  }) => Promise<unknown>;
  setCostBasis: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    costBasis: TakeoffCostBasis;
  }) => Promise<unknown>;
}) {
  const canEdit = useCanEdit();
  const displayValue = tag.unitCost == null ? null : formatCurrency(tag.unitCost);
  const [draftValue, setDraftValue] = useState(
    tag.unitCost == null ? "" : String(tag.unitCost)
  );
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const didCancelRef = useRef(false);
  useOnEditAccessLost(() => {
    setIsEditing(false);
    setDraftValue(tag.unitCost == null ? "" : String(tag.unitCost));
  });
  const effectiveCostBasis = getEffectiveCostBasis(tag.costBasis);

  useEffect(() => {
    setDraftValue(tag.unitCost == null ? "" : String(tag.unitCost));
  }, [tag._id, tag.unitCost]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      didCancelRef.current = false;
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commitValue = async () => {
    if (didCancelRef.current) {
      didCancelRef.current = false;
      setIsEditing(false);
      return;
    }
    setIsEditing(false);
    const trimmedValue = draftValue.trim();
    if (
      trimmedValue !== "" &&
      !UNIT_COST_DECIMAL_PATTERN.test(trimmedValue)
    ) {
      toast.error("Unit cost must be a whole number or have up to 2 decimals");
      setDraftValue(tag.unitCost == null ? "" : String(tag.unitCost));
      return;
    }

    const nextValue = trimmedValue === "" ? null : Number(trimmedValue);

    if (nextValue !== null && (Number.isNaN(nextValue) || nextValue < 0)) {
      toast.error("Unit cost must be zero or greater");
      setDraftValue(tag.unitCost == null ? "" : String(tag.unitCost));
      return;
    }

    if ((tag.unitCost ?? null) === nextValue) {
      return;
    }

    try {
      await setUnitCost({ tagId: tag._id, unitCost: nextValue });
    } catch (error) {
      notifyError(error, "Failed to update unit cost");
      console.error(error);
      setDraftValue(tag.unitCost == null ? "" : String(tag.unitCost));
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      didCancelRef.current = true;
      setDraftValue(tag.unitCost == null ? "" : String(tag.unitCost));
      event.currentTarget.blur();
    }
  };

  const handleCostBasisChange = async (nextCostBasis: TakeoffCostBasis) => {
    if (nextCostBasis === effectiveCostBasis) {
      return;
    }

    try {
      await setCostBasis({ tagId: tag._id, costBasis: nextCostBasis });
    } catch (error) {
      notifyError(error, "Failed to update pricing mode");
      console.error(error);
    }
  };

  if (!canEdit) {
    return (
      <div className="ms-auto grid w-[7rem] grid-cols-[2.5rem_4.25rem] items-center gap-1">
        <span className="w-10 text-center text-[10px] font-semibold leading-none text-muted-foreground">
          {effectiveCostBasis === TAKEOFF_COST_BASIS.count ? "Ct" : "Meas"}
        </span>
        <span className="px-1 text-right font-mono text-[10px] tabular-nums">
          {displayValue === null ? "—" : `$${displayValue}`}
        </span>
      </div>
    );
  }

  return (
    <div className="ms-auto grid w-[7rem] grid-cols-[2.5rem_4.25rem] items-center gap-1">
      <SegmentedControl
        aria-label={`Pricing mode for ${tag.rawValue}`}
        size="compact"
        value={effectiveCostBasis}
        onValueChange={(next) => void handleCostBasisChange(next)}
        disabled={disabled}
        options={[
          {
            value: TAKEOFF_COST_BASIS.count,
            label: "Ct",
            "aria-label": `Use count pricing for ${tag.rawValue}`,
          },
          {
            value: TAKEOFF_COST_BASIS.measurement,
            label: "Meas",
            "aria-label": `Use measurement pricing for ${tag.rawValue}`,
          },
        ]}
        // Stacked to fit the dense results row (two 16px segments in a 36px row).
        className={PRICING_MODE_STACK_CLASSES}
      />
      {isEditing ? (
        <div className="flex h-5 w-[4.25rem] items-center rounded border border-border bg-background px-1">
          <span className="text-[10px] text-muted-foreground">$</span>
          <input
            ref={inputRef}
            aria-label={`Unit cost for ${tag.rawValue}`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={draftValue}
            disabled={disabled}
            onChange={(event) => setDraftValue(event.target.value)}
            onBlur={() => void commitValue()}
            onKeyDown={handleKeyDown}
            className="h-full min-w-0 flex-1 bg-transparent text-right font-mono text-[10px] tabular-nums outline-none"
          />
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setIsEditing(true)}
          className="h-5 w-[4.25rem] rounded px-1 text-right font-mono text-[10px] tabular-nums transition-colors hover:bg-muted"
        >
          {displayValue === null ? "—" : `$${displayValue}`}
        </button>
      )}
    </div>
  );
}

export function ScheduleCellInput({
  tagId,
  header,
  baseValue,
  overrideValue,
  isOverridden,
  preserveEmptyOverride,
  onUpsert,
  onRemoveCells,
}: {
  tagId: Id<"takeoffPilotExtractedTags">;
  header: string;
  baseValue: string;
  overrideValue: string | undefined;
  isOverridden: boolean;
  preserveEmptyOverride: boolean;
  onUpsert: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    cells: Record<string, string>;
  }) => Promise<unknown>;
  onRemoveCells: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    keys: string[];
  }) => Promise<unknown>;
}) {
  const canEdit = useCanEdit();
  const displayValue = overrideValue ?? baseValue;
  const [draftValue, setDraftValue] = useState(displayValue);
  const [isEditing, setIsEditing] = useState(false);
  const didCancelRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  useOnEditAccessLost(() => {
    setIsEditing(false);
    setDraftValue(displayValue);
  });

  useEffect(() => {
    setDraftValue(displayValue);
  }, [displayValue]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      didCancelRef.current = false;
      const el = textareaRef.current;
      el.focus();
      el.select();
    }
  }, [isEditing]);

  const commitValue = async () => {
    if (didCancelRef.current) {
      didCancelRef.current = false;
      setIsEditing(false);
      return;
    }
    setIsEditing(false);
    const trimmed = draftValue.trim();
    const overrideTrimmed = (overrideValue ?? "").trim();
    if (trimmed === overrideTrimmed) {
      setDraftValue(displayValue);
      return;
    }

    if (trimmed === baseValue.trim()) {
      if (isOverridden) {
        try {
          if (preserveEmptyOverride) {
            await onUpsert({ tagId, cells: { [header]: trimmed } });
          } else {
            await onRemoveCells({ tagId, keys: [header] });
          }
        } catch (error) {
          notifyError(error, "Failed to reset schedule cell");
          console.error(error);
          setDraftValue(displayValue);
        }
      }
      return;
    }

    try {
      await onUpsert({ tagId, cells: { [header]: trimmed } });
    } catch (error) {
      notifyError(error, "Failed to update schedule cell");
      console.error(error);
      setDraftValue(displayValue);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      didCancelRef.current = true;
      setDraftValue(displayValue);
      event.currentTarget.blur();
    }
  };

  // Checked before the editor so a demotion mid-edit drops straight to text.
  if (!canEdit) {
    return (
      <span
        className={cn(
          "block min-h-8 w-full rounded-sm px-1.5 py-1 text-left text-[9px] leading-snug whitespace-pre-wrap break-words",
          isOverridden && "bg-status-pending/15"
        )}
        title={isOverridden && overrideValue !== undefined ? `${displayValue} (overridden from "${baseValue}")` : undefined}
      >
        {displayValue || <span className="text-muted-foreground">—</span>}
      </span>
    );
  }

  if (isEditing) {
    return (
      <div className="grid min-h-8 w-full cursor-text whitespace-pre-wrap break-words rounded-sm bg-primary/10 px-1.5 py-1 text-[9px] leading-snug outline outline-2 outline-primary">
        <textarea
          ref={textareaRef}
          rows={1}
          aria-label={`Edit ${header}`}
          value={draftValue}
          onChange={(event) => setDraftValue(event.target.value)}
          onBlur={() => void commitValue()}
          onKeyDown={handleKeyDown}
          className="col-start-1 row-start-1 h-full w-full min-w-0 resize-none overflow-hidden border-0 bg-transparent p-0 font-[inherit] text-[9px] leading-snug text-foreground outline-none"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none invisible col-start-1 row-start-1 whitespace-pre-wrap break-words"
        >
          {getTextareaMirrorValue(draftValue)}
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      className={cn(
        "block min-h-8 w-full rounded-sm px-1.5 py-1 text-left text-[9px] leading-snug whitespace-pre-wrap break-words transition-colors hover:bg-muted",
        isOverridden && "bg-status-pending/15"
      )}
      title={
        displayValue
          ? isOverridden
            ? `${displayValue} (overridden${overrideValue !== undefined ? ` from "${baseValue}"` : ""})`
            : displayValue
          : "Click to edit"
      }
    >
      {displayValue || <span className="text-muted-foreground">—</span>}
    </button>
  );
}

export function CustomLineItemNameInput({
  tag,
  disabled,
  renameCustomTag,
}: {
  tag: TagRecord;
  disabled: boolean;
  renameCustomTag: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    rawValue: string;
  }) => Promise<unknown>;
}) {
  const canEdit = useCanEdit();
  const [draftValue, setDraftValue] = useState(tag.rawValue);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const didCancelRef = useRef(false);
  useOnEditAccessLost(() => {
    setIsEditing(false);
    setDraftValue(tag.rawValue);
  });

  useEffect(() => {
    setDraftValue(tag.rawValue);
  }, [tag._id, tag.rawValue]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }
    didCancelRef.current = false;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const commitValue = async () => {
    if (didCancelRef.current) {
      didCancelRef.current = false;
      setIsEditing(false);
      return;
    }
    const trimmedValue = draftValue.trim();
    if (!trimmedValue) {
      toast.error("Custom line item name is required");
      setDraftValue(tag.rawValue);
      setIsEditing(false);
      return;
    }

    if (trimmedValue === tag.rawValue) {
      setIsEditing(false);
      return;
    }

    try {
      await renameCustomTag({ tagId: tag._id, rawValue: trimmedValue });
      setIsEditing(false);
    } catch (error) {
      notifyError(error, "Failed to rename custom line item");
      console.error(error);
      setDraftValue(tag.rawValue);
      setIsEditing(false);
    }
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
    if (event.key === "Escape") {
      didCancelRef.current = true;
      setDraftValue(tag.rawValue);
      event.currentTarget.blur();
    }
  };

  if (!canEdit) {
    return (
      <span className="block max-w-full truncate text-left text-[11px] font-medium" title={tag.rawValue}>
        {tag.rawValue}
      </span>
    );
  }

  if (!isEditing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsEditing(true)}
        className="block max-w-full truncate text-left text-[11px] font-medium hover:underline disabled:hover:no-underline"
        aria-label={`Edit custom line item ${tag.rawValue}`}
        title={tag.rawValue}
      >
        {tag.rawValue}
      </button>
    );
  }

  return (
    <div className="grid max-w-full min-w-0 cursor-text rounded-sm bg-primary/10 px-1 py-0.5 outline outline-2 outline-primary">
      <input
        ref={inputRef}
        aria-label={`Custom line item name for ${tag.rawValue}`}
        type="text"
        autoComplete="off"
        value={draftValue}
        disabled={disabled}
        onChange={(event) => setDraftValue(event.target.value)}
        onBlur={() => void commitValue()}
        onKeyDown={handleInputKeyDown}
        className="h-5 w-full min-w-0 border-0 bg-transparent p-0 text-[11px] font-medium text-foreground outline-none disabled:cursor-not-allowed disabled:opacity-50"
      />
    </div>
  );
}

export function TagColorInput({
  tag,
  resolvedColorHex,
  disabled,
  setColorHex,
}: {
  tag: TagRecord;
  resolvedColorHex: string;
  disabled: boolean;
  setColorHex: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    colorHex: string;
  }) => Promise<unknown>;
}) {
  const canEdit = useCanEdit();
  const [draftColorHex, setDraftColorHex] = useState(resolvedColorHex);

  useEffect(() => {
    setDraftColorHex(resolvedColorHex);
  }, [resolvedColorHex]);

  const handleColorChange = async (nextColor: string) => {
    const normalizedColor = nextColor.toUpperCase();
    const previousColor = draftColorHex;
    setDraftColorHex(normalizedColor);

    if (normalizedColor === resolvedColorHex.toUpperCase()) {
      return;
    }

    try {
      await setColorHex({ tagId: tag._id, colorHex: normalizedColor });
    } catch (error) {
      setDraftColorHex((currentColor) =>
        currentColor.toUpperCase() === normalizedColor
          ? previousColor
          : currentColor
      );
      notifyError(error, "Failed to update tag color");
      console.error(error);
    }
  };

  if (!canEdit) {
    return (
      <span
        aria-hidden="true"
        className="size-4 rounded-sm border border-border"
        style={{ backgroundColor: draftColorHex }}
      />
    );
  }

  return (
    <ColorPicker
      value={draftColorHex}
      ariaLabel={`Set color for ${tag.rawValue}`}
      disabled={disabled}
      onChange={(nextColor) => {
        void handleColorChange(nextColor);
      }}
    />
  );
}
